import React, { useState } from "react";
import apiFetch from "../../utils/apiFetch";
import SmModal, { ConfirmModal } from "./SmModal";
import FieldForm from "./FieldForm";
import MaintenanceSection from "./MaintenanceSection";

export default function EquipmentTab({ substationId, config, items = [], canCreate, canEdit, canDelete, onRefresh }) {
  const [expanded, setExpanded] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  function openCreate() {
    setForm({});
    setEditItem(null);
    setModalMode("create");
  }

  function openEdit(item) {
    const vals = {};
    config.fields.forEach((f) => {
      vals[f.key] = item[f.key] ?? "";
    });
    setForm(vals);
    setEditItem(item);
    setModalMode("edit");
  }

  async function handleSave() {
    const required = config.fields.filter((f) => f.required);
    for (const f of required) {
      if (!form[f.key]?.toString().trim()) return alert(`${f.label} киритилиши шарт`);
    }
    setSaving(true);
    try {
      const url =
        modalMode === "create"
          ? `/api/substation-management/${substationId}/equipment/${config.slug}`
          : `/api/substation-management/${substationId}/equipment/${config.slug}/${editItem.id}`;
      const method = modalMode === "create" ? "POST" : "PATCH";
      const res = await apiFetch(url, { method, body: form });
      if (!res.ok) throw new Error((await res.json()).message);
      setModalMode(null);
      onRefresh();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    try {
      const res = await apiFetch(
        `/api/substation-management/${substationId}/equipment/${config.slug}/${deleteTarget.id}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error((await res.json()).message);
      setDeleteTarget(null);
      onRefresh();
    } catch (e) {
      alert(e.message);
    }
  }

function getMaintRecords(item) {
  for (const key of Object.keys(item)) {
    if (key.includes("maintenance") && Array.isArray(item[key])) return item[key];
  }
  return [];
}

function shouldShowGroup(fields, field, index) {
  return field.group && fields.findIndex((item) => item.group === field.group) === index;
}

  return (
    <div className="sm-equip sm-equip--detail">
      <div className="sm-equip__header">
        <h3 className="sm-equip__title">
          {config.label}
          <span className="sm-equip__count">({items.length})</span>
        </h3>
        {canCreate && (
          <button type="button" className="sm-btn sm-btn--primary sm-btn--sm" onClick={openCreate}>
            Қўшиш
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="sm-detail-empty">Ҳали {config.label.toLowerCase()} қўшилмаган</div>
      ) : (
        items.map((item) => (
          <article key={item.id} className="sm-equip-row">
            <button type="button" className="sm-equip-row__head" onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
              <span className="sm-equip-row__name">{item.name || item.battery_type || `#${item.id}`}</span>
              <span className="sm-equip-row__meta">
                {config.columns.slice(1, 3).map((col) => (
                  <span key={col}>{item[col] || "—"}</span>
                ))}
              </span>
              <span className={`sm-equip-row__toggle ${expanded === item.id ? "is-open" : ""}`}>&#9660;</span>
            </button>

            {expanded === item.id && (
              <div className="sm-equip-row__body">
                <div className="sm-form sm-form--detail sm-form--compact">
                  {config.fields.map((f, index) => (
                    <React.Fragment key={f.key}>
                      {shouldShowGroup(config.fields, f, index) && <div className="sm-detail-field-group-title">{f.group}</div>}
                      <div className={`sm-field${f.type === "textarea" ? " sm-form__full" : ""}`}>
                        <span className="sm-field__label">{f.label}</span>
                        <span className="sm-detail-field__value">{item[f.key] === null || item[f.key] === undefined || item[f.key] === "" ? "—" : item[f.key]}</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                <MaintenanceSection
                  substationId={substationId}
                  equipmentType={config.slug}
                  equipmentId={item.id}
                  maintTable={config.maintTable}
                  records={getMaintRecords(item)}
                  canCreate={canCreate}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  onRefresh={onRefresh}
                />

                <div className="sm-equip-row__actions">
                  {canEdit && (
                    <button type="button" className="sm-btn sm-btn--ghost sm-btn--sm" onClick={() => openEdit(item)}>
                      Таҳрирлаш
                    </button>
                  )}
                  {canDelete && (
                    <button type="button" className="sm-btn sm-btn--danger sm-btn--sm" onClick={() => setDeleteTarget(item)}>
                      Ўчириш
                    </button>
                  )}
                </div>
              </div>
            )}
          </article>
        ))
      )}

      {modalMode && (
        <SmModal
          title={modalMode === "create" ? `${config.label} — янги` : `${config.label} — таҳрирлаш`}
          onClose={() => setModalMode(null)}
          wide
          footer={
            <>
              <button type="button" className="sm-btn sm-btn--ghost" onClick={() => setModalMode(null)}>
                Бекор
              </button>
              <button type="button" className="sm-btn sm-btn--primary" onClick={handleSave} disabled={saving}>
                {saving ? "Сақланмоқда..." : "Сақлаш"}
              </button>
            </>
          }
        >
          <FieldForm fields={config.fields} values={form} onChange={setForm} className="sm-form sm-form--detail" />
        </SmModal>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Ўчириш"
          text={`"${deleteTarget.name || deleteTarget.battery_type || `#${deleteTarget.id}`}" ўчирмоқчимисиз?`}
          subText="Маълумот аудит-логга сақланади."
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
